<template>
  <div :class="['content', programStyle]">
    <div class="title-container">
      <div class="title">Survey Interference Form</div>
      <ul class="dealer-info info-box">
        <li>
          <span>{{ $t('date') }}:</span> {{ otherAppealInfo.formCreatedAt | date }}
        </li>
        <li v-if="otherAppealInfo.statuses[0].result === 'Required'">
          <span
            >{{ $t('si-info')
            }}<span v-if="this.otherAppealInfo.interferenceMetaData" class="dealer-name">
              {{ this.otherAppealInfo.interferenceMetaData.dealerPrincipalName }}</span
            ><span v-else class="dealer-name">
              {{ this.otherAppealInfo.userInfo[0].firstName }} {{ this.otherAppealInfo.userInfo[0].lastName }}</span
            >
          </span>
        </li>
        <li v-if="otherAppealInfo.statuses[0].result === 'Acknowledged'">
          <span
            >{{ $t('si-info')
            }}<span v-if="this.otherAppealInfo.interferenceMetaData" class="dealer-name">
              {{ this.otherAppealInfo.interferenceMetaData.dealerPrincipalName }}</span
            ><span v-else class="dealer-name"> {{ this.otherAppealInfo.statuses[0].name }}</span>
          </span>
        </li>
        <li>
          <span>{{ $t('dealer-name-selecteddealer-dealername') }}</span> {{ selectedDealer.dealerName }}
        </li>
        <li>
          <span>{{ $t('dealer-code-1') }}</span> {{ selectedDealer.entity }}
        </li>
      </ul>
    </div>
    <form class="form-container flex-column" @submit.prevent="save">
      <!-- Display new the SI letters if interferenceMetaData is returned in the end point response -->
      <div v-if="this.otherAppealInfo.interferenceMetaData" class="letter-container">
        <p class="section-title">Letter</p>
        <p v-if="this.otherAppealInfo.interferenceType === 'first' || this.otherAppealInfo.interferenceType === false">
          FCA US LLC (“FCA) is providing your dealership notice that FCA believes that it has detected surveys tied to
          your dealership that must be removed due to survey interference. This is your
          <b>{{ this.otherAppealInfo.interferenceMetaData.infractionName.toLowerCase() }} infraction</b>; you are now
          entering a grace period (Beginning {{ this.otherAppealInfo.interferenceMetaData.gracePeriodStart }} through
          {{ this.otherAppealInfo.interferenceMetaData.gracePeriodEnd }}.) The intention of the grace period is to allow
          your dealership time to take corrective measures to ensure interference activity does not continue after the
          conclusion of the grace period. When the grace period concludes, you will enter a probationary period which
          will begin in the month of {{ this.otherAppealInfo.interferenceMetaData.probationStart }} and end in the month
          of {{ this.otherAppealInfo.interferenceMetaData.probationEnd }}. During this time, any additional infractions
          will result in escalation. The details below list the specific records that have been flagged for survey
          interference. As a result, FCA is taking the following actions:
        </p>
        <p v-else-if="this.otherAppealInfo.interferenceType === 'followUp'">
          FCA US LLC (“FCA) is providing your dealership notice <b>as a follow up to your first infraction</b> as FCA
          believes that it has detected surveys tied to your dealership that must be removed due to survey interference.
          <b
            >Your dealership is currently in the grace period, and therefore continued interference will not result in
            escalation at this time.</b
          >
          Your grace period will conclude after {{ this.otherAppealInfo.interferenceMetaData.gracePeriodEnd }}. When the
          grace period concludes, you will enter a probationary period which will begin in the month of
          {{ this.otherAppealInfo.interferenceMetaData.probationStart }} and end in the month of
          {{ this.otherAppealInfo.interferenceMetaData.probationEnd }}. During this time, any additional infractions
          will result in escalation. The attached lists the specific records that have been flagged for survey
          interference. As a result, FCA is taking the following actions:
        </p>
        <p v-else-if="this.otherAppealInfo.interferenceType === 'second'">
          FCA US LLC (“FCA) is providing your dealership notice that FCA believes that it has detected surveys tied to
          your dealership that must be removed due to survey interference. This is your
          <b>{{ this.otherAppealInfo.interferenceMetaData.infractionName.toLowerCase() }} infraction</b>; your
          dealership is ineligible to earn Customer First Incentives from
          {{ this.otherAppealInfo.interferenceMetaData.penaltyPeriodStart }} –
          {{ this.otherAppealInfo.interferenceMetaData.penaltyPeriodEnd }}. Your probationary period is now
          {{ this.otherAppealInfo.interferenceMetaData.probationStart }} –
          {{ this.otherAppealInfo.interferenceMetaData.probationEnd }}. During this time, any additional infractions
          will extend the ineligibility window and probationary period. The details below list the specific records that
          have been flagged for survey interference. As a result, FCA is taking the following actions:
        </p>
        <ul>
          <li>
            Answered surveys identified as interference will be removed from Customer First performance metric
            calculations. Revised scores will be posted in the Customer First Incentives Dashboard on DealerConnect.
          </li>
          <li>
            All surveys identified as interference will be treated as a record with a missing email, which will be
            reflected in email capture calculations. Surveys flagged for interference have been shared with FCA teams
            that use customer survey data, including but not necessarily limited to, Rewarding Excellence, Delivery
            Allowance, Mopar Service Incentives, Sales, SmartWarranty, and Performance Institute; this includes any
            other incentive programs that are tied to Customer First results. These departments may use this data to
            adjust scores used for incentives/benefits associated with their programs and may apply additional
            consequences at their own discretion.
          </li>
          <li>
            Answered surveys identified as interference will be notated in the Customer Experience (Survey) Dashboard on
            the Sales/Service Disposition Detail Report and labeled as “Survey Interference”, “Not Included in Scoring”
            along with the original disposition of “Included in Scoring” in the “Complete Detail” column of the report.
            The scores will also be removed from the dealer’s final month’s scoring.
          </li>
          <li>
            Surveys identified as “Blocking” are presented so you can see where a unique email address has been applied
            to multiple customer records. The email is a valid email, meaning, it is a made-up email that has a valid
            .com address; yet, the customer surveys were never answered. This is a missed opportunity for your
            dealership and FCA for several reasons: 1. Your customer records in COIN now have an incorrect email
            attached to their ID. They will never receive communications from you or us until someone manually updates
            their record. 2. Your survey return counts will be missing out on these surveys, as you will never get a
            customer response. 3. These missed scores could hinder your ability to earn incentives, as having ample
            survey returns is key.
          </li>
          <li>
            A representative from your Business Center may contact you to further discuss this matter. If you believe
            this concern is unwarranted, please provide the Business Center with the appropriate documentation to
            substantiate this claim and file an appeal.
          </li>
        </ul>
        <p>
          FCA will continue to monitor survey interference activity on a monthly basis. In the meantime, FCA advises you
          to take steps to cease survey interference activity in your dealerships as soon as possible. Continued
          participation in survey interference may result in additional consequences, including (but not necessarily
          limited to) dealer ineligibility for Customer First, Wagoneer and other FCA program incentives.<b>
            Please refer to the Survey Interference Resource Guide for more information on the escalation process.</b
          >
        </p>
        <p>
          Please take necessary actions to correct this situation immediately. If you have questions, please feel free
          to reach out to your Business Center. Thank you for your assistance in ensuring we have accurate owner
          information as we strive to ensure our customers receive a world class experience.
        </p>
        <div class="signatory">
          <p>Sincerely,</p>
          <p>Alayna Schraffenberger</p>
          <p>Head of NA Sales & After-Sales CX</p>
        </div>
      </div>
      <!-- Display the old/legacy SI letter if interferenceMetaData is not returned in the end point response -->
      <div v-else class="letter-container">
        <p>
          FCA US LLC (“FCA”) is providing your dealership notice that FCA believes that it has detected surveys tied to
          your dealership that must be removed due to survey interference. The attached lists the specific records that
          have been flagged for survey interference. As a result, FCA is taking the following actions:
        </p>
        <ul>
          <li>
            Answered Surveys identified as interference will be removed from Customer First performance metrics
            calculations. Revised scores will be posted in the Customer First Incentives Dashboard on DealerConnect.
          </li>
          <li>
            Surveys flagged for interference have been shared with FCA teams that use customer survey data, including
            but not necessarily limited to, Rewarding Excellence, Mopar Service Incentives, Sales, SmartWarranty and
            Performance Institute. These departments may use this data to adjust scores used for incentives/benefits
            associated with their programs and may apply additional consequences at their own discretion.
          </li>
          <li>
            Answered Surveys identified as interference will be notated in the Customer Experience (Survey) Dashboard on
            the Sales/Service Disposition Detail Report and labeled as “Survey Interference – Not Included in Scoring”
            along with the original disposition of “Included in Scoring” in the “Complete Detail” column on the report.
            The scores will also be removed from the dealer’s final month’s scoring.
          </li>
          <li>
            Surveys identified as “Blocking” are presented so you can see where a unique email address has been applied
            to multiple customer records. The email is a valid email, meaning, it is a made up email that has a valid
            .com address; yet, the customer surveys were never answered. This is a missed opportunity for your
            dealership and Stellantis for several reasons: 1. Your customer records in COIN now have an incorrect email
            attached to their ID. They will never receive communications from you or us until someone manually updates
            their record. 2. Your survey return counts will be missing out on these surveys, as you will never get a
            customer response. 3. These missed scores could hinder your ability to earn incentives, as having ample
            survey returns is key.
          </li>
          <li>
            A representative from your Business Center may contact you to further discuss this matter. If you believe
            this concern is unwarranted, please provide the Business Center with the appropriate documentation to
            substantiate this claim.
          </li>
        </ul>
        <p>
          FCA will continue to monitor survey interference activity on a monthly basis. In the meanwhile, FCA advises
          you to take steps to cease survey interference activity in your dealership as soon as possible. Continued
          participation in survey interference may result in additional consequences, including (but not necessarily
          limited to) dealer ineligibility for Customer First, Wagoneer and other FCA program incentives. To address
          issues surrounding survey interference moving forward, FCA strongly encourages you to do the following:
        </p>
        <ul>
          <li>Speak with all dealership employees regarding the importance of data integrity.</li>
          <li>
            As part of the discussion, review your dealership’s process for confirming and collecting accurate customer
            information in FCA systems (i.e., NVDR, COIN via DealerConnect)
          </li>
          <li>
            If the customer records associated with interference contain inaccurate customer email addresses, and if
            your dealership is certain that it has correct customer email addresses based on NVDR records or direct
            contact with the customer, please make the corrections directly into COIN via DealerConnect. (Please note
            that updates via wiAdvisor and your DMS system will not update COIN.)
          </li>
          <li>
            If your dealership does not have the correct email address, do not make any updates in COIN at this time.
            Wait until the customer returns to the dealership for another sales or service event. At that point, request
            the correct contact information from the customer and update it directly into COIN that day.
          </li>
          <li>
            If a new sales or service customer does not wish to provide his/her email address, please leave the email
            address field blank on the NVDR and/or in COIN. Do not enter any placeholders in the email address field.
          </li>
        </ul>
        <p>
          Please take the necessary actions to correct this situation immediately. If you have questions, please feel
          free to reach out to your business center. Thank you for your assistance in ensuring we have accurate owner
          information as we strive to ensure our customers receive a world class experience.
        </p>
        <div class="signatory">
          <p>Sincerely,</p>
          <p>Scott Duschinsky</p>
          <p>Head of NA Sales & After-Sales CX</p>
        </div>
      </div>
      <div class="interference-description">
        <p class="section-title">Method of interference description:</p>
        <ul class="info-box">
          <li><span>Email:</span> Incorrect email for customer.</li>
          <li><span>IP:</span> Duplicate IP Address (internet access point or network).</li>
          <li><span>FP:</span> Duplicate Digital Fingerprint (unique device – desktop, laptop, iPad, Phone, etc.).</li>
          <li><span>Blocked:</span> Multiple unanswered records, tied to multiple/same email address(es).</li>
          <li>
            <span>Direct Interference:</span> Interference identified is tied directly to selling or servicing dealer.
          </li>
          <li>
            Please see the official Customer First Program Rules for additional details regarding Survey Interference.
          </li>
        </ul>
      </div>
      <DataTable id="siTable" class="data-table" :headings="siTH" :rows="rows" :perPage="rowCount" :page="currentPage">
      </DataTable>
      <div class="bottom-container flex-column">
        <p class="section-title">{{ $t('acknowledged-by') }}</p>
        <div class="bottom-info flex-column">
          <div class="flex-row input-info">
            <div class="flex-column">
              <span class="label">* {{ $t('name') }}:</span>
              <input v-if="otherAppealInfo.statuses[0].result === 'Required'" readonly v-model="this.name" />
              <input v-else readonly v-model="this.otherAppealInfo.statuses[0].name" />
            </div>
            <div class="flex-column">
              <span class="label">{{ $t('date') }}:</span>
              <div class="date">
                <span v-if="otherAppealInfo.statuses[0].result === 'Required'">{{ date | date }}</span>
                <span v-else>{{ otherAppealInfo.statuses[0].createdAt | date }}</span>
              </div>
            </div>
          </div>
          <p class="section-label">* Signature:</p>
          <div class="signature-section flex-column">
            <div class="flex-row signature-input">
              <div class="signature-icon"><SignatureIcon /></div>
              <VueSignaturePad id="signature" width="400px" height="100px" ref="signaturePad" :options="options" />
            </div>
            <span class="signature-confirmation" v-if="signature">{{ $t('signature-saved') }}</span>
            <div class="signature-buttons flex-row">
              <span class="custom-input-buttons flex-row">
                <span class="edit-button" :class="[isAcknowledged ? 'disabled' : '']" @click="saveSignature">{{
                  $t('save-signature')
                }}</span
                ><span class="separator">|</span
                ><span class="edit-button" :class="[isAcknowledged ? 'disabled' : '']" @click="clearSignature">{{
                  $t('clear')
                }}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="button-container">
          <button
            v-if="mostRecentStatus.result !== 'Acknowledged'"
            value="save"
            :class="[fullValidationCheck ? '' : 'disabled', '']"
            :disabled="!fullValidationCheck"
          >
            {{ $t('submit') }}
          </button>
          <button class="cancel-button" @click="exit">{{ $t('exit') }}</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapState } from 'vuex';
import siTH from '@/utils/tableHeaders/siTH';
import SignatureIcon from '@/assets/icons/delete-icn.svg';

export default {
  name: 'SurveyInterferenceForm',
  components: { DataTable, SignatureIcon },
  props: {
    appealType: {
      type: String,
      required: true,
    },
    entity: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      date: new Date(),
      rowCount: 999,
      currentPage: 1,
      signatureSaved: false,
      signature: null,
      value: 'Survey-Interference',
      options: {
        penColor: '#000000',
      },
      siTH: siTH(),
    };
  },
  methods: {
    save() {
      const loader = this.$loading.show();

      const { entity, domain, signature } = this;
      const { formId, interferenceMetaData, interferenceType } = this.otherAppealInfo;

      const params = {
        formId,
        domain,
        entity,
        fieldSignature: signature,
        type: 'Survey-Interference',
        result: this.$t('acknowledged'),
        interferenceMetaData,
        interferenceType,
      };
      this.updateForm(params);
      loader.hide();
    },
    updateForm(params) {
      const loader = this.$loading.show();

      this.$store
        .dispatch('updateForm', params)
        .then(() => {
          this.$store.state.surveyInterference = false;
          this.$notify({
            group: 'primary',
            type: 'success',
            text: 'Survey Interference Letter Successfully Acknowledged!',
          });
          this.exit();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    exit() {
      if (this.$route.name === 'Survey-Interference-Letter') {
        this.$router.push({
          name: 'DealerSI',
        });
      } else if (this.$route.name === 'Survey-Interference-Letter-Field') {
        this.$router.push({
          name: 'Summary',
        });
      } else {
        this.$router.push({
          name: 'Dealer-Forms',
        });
      }
    },
    getUrl(res) {
      const upload = {
        originalFilename: res.res.data.fileInfo.originalFilename,
        adjustedFilename: res.res.data.fileInfo.adjustedFilename,
        label: res.appeal.dialTitle,
        dialId: res.appeal.dialId,
      };
      this.uploads.push(upload);
    },
    saveSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (!isEmpty) {
        this.signature = data;
      }
    },
    clearSignature() {
      this.$refs.signaturePad.clearSignature();
      this.signature = null;
    },
    getSignatureImage(url) {
      if (url) {
        fetch(url)
          .then((raw) => raw.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
              this.$refs.signaturePad.fromDataURL(reader.result);
              this.$refs.signaturePad.lockSignaturePad();
            };
          });
      } else {
        this.$notify({
          group: 'primary',
          type: 'error',
          text: 'Signature not found',
        });
      }
    },
    getEntity() {
      const loader = this.$loading.show();
      const { domain, entity } = this;

      this.$store.dispatch('global/getEntity', { domain, entity }).then((res) => {
        if (res) {
          this.$store.state.selectedDealer = res;
        }
        loader.hide();
      });
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['selectedForm', 'otherAppealInfo', 'selectedDealer', 'surveyInterference', 'savedSignature']),
    isAcknowledged() {
      return this.otherAppealInfo.statuses[0].result === 'Acknowledged';
    },
    domain() {
      return this.programName;
    },
    rows() {
      if (this.otherAppealInfo && this.otherAppealInfo.interferenceRecords) {
        return this.otherAppealInfo.interferenceRecords;
      }
      return null;
    },
    mostRecentStatus() {
      if (this.otherAppealInfo) {
        return this.otherAppealInfo.statuses[0];
      }
      return null;
    },
    fullValidationCheck() {
      if (this.signature && this.name) {
        return true;
      }
      return false;
    },
  },
  watch: {},
  created() {
    if (this.$route.name === 'Survey-Interference-Letter-Field') {
      this.getEntity();
    }
    this.name = `${this.otherAppealInfo.userInfo[0].firstName} ${this.otherAppealInfo.userInfo[0].lastName}`;
    if (this.isAcknowledged) {
      this.getSignatureImage(this.otherAppealInfo.savedSignature);
    }
  },
  beforeDestroy() {
    this.$emit('exit');
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
  padding: 30px;
}
.title-container {
  margin-bottom: 30px;
  .title {
    color: $primary;
    font-size: 18px;
    line-height: 24px;
    font-weight: bold;
    text-transform: uppercase;
  }
}
.info-box {
  border: 1px solid $primary-black;
  padding: 20px;
  list-style-type: none;
  margin: 30px 0px 0px;
  min-width: 704px;
  display: inline-block;
  li {
    margin-bottom: 15px;
    font-size: 14px;
    line-height: 20px;
    span {
      font-weight: bold;
    }
    .dealer-name {
      font-weight: normal;
    }
  }
  li:last-child {
    margin-bottom: 0px;
  }
}
.section-title {
  font-weight: bold;
  text-transform: uppercase;
  margin: 0px;
}
.title-small {
  font-size: 16px;
  margin-bottom: 20px;
}
.item-container {
  display: flex;
  margin: 0 30px 30px 30px;
}
.subtitle {
  font-size: 14px;
  margin-bottom: 10px;
}
.description {
  margin: 10px 0;
}
.bottom-container {
  margin-top: 30px;
  .section-title {
    color: $primary;
    font-size: 18px;
    line-height: 24px;
    font-weight: bold;
    text-transform: uppercase;
    margin: 0px;
  }
  .bottom-info {
    .input-info {
      .flex-column {
        margin-right: 40px;
      }
      margin-top: 30px;
      .label {
        margin-bottom: 10px;
      }
      .custom-input {
        border: 1px solid $border;
        width: 289px;
        height: 47px;
        justify-content: space-between;
        input {
          border: none;
          padding: 0px 0px 0px 15px;
          height: 100%;
          outline: none;
          margin-right: 0px;
        }
        .custom-input-buttons {
          padding: 0px 15px;
        }
      }
    }
    .section-label {
      font-weight: bold;
      text-transform: uppercase;
      margin: 30px 0px 20px;
    }
  }
  .button-container {
    display: flex;
    margin: 45px 0px 15px;
    align-items: center;
    justify-content: flex-start;
    button {
      margin-right: 15px;
    }
  }
}
.date {
  width: 289px;
  background: $input-readonly;
  border: 1px solid $border;
  padding: 13px 0px 13px 20px;
  span {
    color: $input-readonly-text;
  }
}
.form-container {
  .letter-container {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 30px;
    ul {
      margin: 0px;
      padding-inline-start: 15px;
      li {
        margin-bottom: 15px;
      }
    }
    .signatory {
      p {
        margin: 0px;
      }
    }
  }
  .interference-description {
    margin-bottom: 30px;
  }
}
.data-table {
  margin: 0px -30px;
  ::v-deep {
    td {
      font-size: 12px;
    }
  }
}
.flex-column {
  span {
    margin-bottom: 0px;
  }
}
.signature-section {
  width: 464px;
  border: 1px solid #a3a3a3;
}
.signature-input {
  align-items: flex-end;
  margin: 0px 40px;
  .signature-icon {
    margin-right: 10px;
    svg {
      height: 12px;
      width: 12px;
      path {
        fill: #d1d1d1 !important;
      }
    }
  }
  #signature {
    background: $primary-white;
    border-bottom: solid 1px #d1d1d1;
    width: 350px !important;
    ::v-deep {
      canvas {
        width: 350px !important;
      }
    }
  }
}
.signature-confirmation {
  font-size: 12px;
  margin-left: 60px;
}
.signature-buttons {
  justify-content: flex-end;
  margin-top: 20px;
  .right-btn {
    margin-left: 20px;
  }
}
.custom-input-buttons {
  padding: 0px 15px 15px;
  align-items: center;
  .separator {
    color: #d1d1d1;
    margin: 0px 7.5px;
  }
  .edit-button {
    font-size: 14px;
    color: #243882;
    cursor: pointer;
  }
  .disabled {
    opacity: 0.7;
    cursor: default;
    pointer-events: none;
  }
}
.cancel-button {
  background-color: $primary-white;
  border: 1px solid $primary;
  color: $primary;
}
</style>
